import { AlertTriangle, RefreshCw, Home, WifiOff } from "lucide-react";

interface BrandedErrorPageProps {
  error?: unknown;
  /** Optional reset handler from an error boundary. */
  resetError?: () => void;
}

/**
 * Branded full-screen error page shown when the app crashes.
 * Tells the user plainly whether it looks like their connection or our side,
 * and what to do next.
 */
export default function BrandedErrorPage({ error, resetError }: BrandedErrorPageProps) {
  const message = String((error as Error)?.message ?? error ?? "");
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;
  const looksNetwork =
    offline || /network|failed to fetch|load failed|timeout|offline/i.test(message);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-accent/5 p-6">
      <div className="max-w-md w-full text-center space-y-4">
        <div className="mx-auto w-14 h-14 rounded-2xl bg-destructive/10 flex items-center justify-center">
          {looksNetwork ? (
            <WifiOff className="w-7 h-7 text-destructive" />
          ) : (
            <AlertTriangle className="w-7 h-7 text-destructive" />
          )}
        </div>

        <h1 className="text-xl font-bold">
          {looksNetwork ? "You appear to be offline" : "Something went wrong on our side"}
        </h1>

        <p className="text-sm text-muted-foreground">
          {looksNetwork ? (
            <>
              We couldn't reach Fantasy Football Africa. Check your mobile data or Wi-Fi and try
              again — nothing you did is lost.
            </>
          ) : (
            <>
              Our team has been notified automatically and is already looking into it. Please
              reload the page. If it keeps happening, contact support from your dashboard.
            </>
          )}
        </p>

        <div className="flex flex-col sm:flex-row gap-2 justify-center pt-1">
          <button
            onClick={() => {
              resetError?.();
              window.location.reload();
            }}
            className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium inline-flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-4 h-4" /> Try again
          </button>
          <button
            onClick={() => (window.location.href = "/")}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium inline-flex items-center justify-center gap-2"
          >
            <Home className="w-4 h-4" /> Go home
          </button>
        </div>

        {import.meta.env.DEV && message && (
          <pre className="text-left text-[10px] text-muted-foreground overflow-auto max-h-40 mt-3">
            {message}
          </pre>
        )}
      </div>
    </div>
  );
}
